import ApplicationSerializer from './application';
import { camelize } from '@ember/string';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type {
  UserProfileWellnessTrainComponentType,
  UserProfileWellnessTrainComponentName,
} from 'tio-common/models/wellness-insights-component-definition';

// NB can't assume this is complete until
interface WellnessTrainData extends Record<string, string | number | boolean> {
  wellness_train: string;
  wellness_train_rating: number;
  wellness_train_description: string;
  balance_updated_at: string;
  obs_updated_at: string;
  calculation_updated_at: string;
  // TODO: find out what 'actions' is...? this isn't written down anywhere - james 20250725
  // actions: unknown;
  nslds: boolean;
  nslds_as_of: string;
  nslds_processed_as_of: string;
  has_ffel_loans: boolean;
  has_direct_loans: boolean;
  has_perkins_loans: boolean;
  has_parentplus_loans: boolean;
  in_grace_period_or_deferment: boolean;
  non_optimal_repayment_plan: boolean;
  idr_repayment_plan: boolean;
  pslf_count_shown: boolean;
  idr_anniversary_date: string;
  balance_going_down: boolean;
  has_recent_forbearance: boolean;
  has_default_history: boolean;
  currently_in_default: boolean;
  pslf_form_submitted_date: string;
  pslf_form_submitted: boolean;
  pslf_submitted_within11m: boolean;
  pslf_submitted_within4m: boolean;
  file_type: string;
  calc: string;
  version: string;
  statement_balance_updated_at: string;
  statement_obs_updated_at: string;
}

interface ComponentDefinitionData extends Record<string, string | number> {
  name: UserProfileWellnessTrainComponentName;
  sub_title: string;
  text: string;
  type: UserProfileWellnessTrainComponentType;
}

type WellnessTrainPayload = WellnessTrainData & {
  components?: ComponentDefinitionData[];
};

/**
 * serializes the wellness-train model (sorta fake, it's not a real resource but it's useful
 * to be able to treat it like other models)
 *
 * @memberof serializers
 */
export default class WellnessTrainSerializer extends ApplicationSerializer {
  normalizeResponse(
    store: Store,
    _primaryModelClass: ModelSchema<unknown>,
    payload: WellnessTrainPayload,
    id: string,
    _requestType: string
  ) {
    // get user id to use in relationships
    const userId = payload.userId;
    delete payload.userId;

    // generate id
    const trainId = id || `user-${userId}-wellness-train`;
    // NOTE: keeping wellness_train as an attribute so we can check the train type

    // extract component definitions separately and extend with id and order attrs; order
    // matters for the dashboard UI, so it needs needs to be modeled somehow if the data
    // from the payload components array gets process into a relational model
    const componentDefinitions = (payload.components || []).map((definition, idx) => {
      const componentId = `user-${userId}-wellness-train-component-definition-${definition.type}`;
      return { id: componentId, order: idx, ...definition };
    });
    delete payload.components;

    // build the normalized data for the wellness train and associate with user
    const data = {
      id: trainId,
      type: 'wellness-train',
      attributes: this._normalizeAttributes(payload),
      relationships: {
        user: { data: { type: 'user', id: userId } },
        componentDefinitions: {
          data: componentDefinitions.map((component: ComponentDefinitionData) => ({
            type: 'wellness-insights-component-definition',
            id: component.id,
          })),
        },
      },
    };

    // push component definitions into the store
    componentDefinitions.forEach((component: ComponentDefinitionData & { id: string }) => {
      store.push({
        data: [
          {
            type: 'wellness-insights-component-definition',
            id: component.id,
            attributes: this._normalizeAttributes(component),
          },
        ],
      });
    });

    return { data };
  }

  _normalizeAttributes(
    obj: Record<string, string | number | boolean>
  ): Record<string, string | number | boolean> {
    const normalized: Record<string, string | number | boolean> = {};
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        // 'sub_title'...?? when subtitle is an actual word? just call this 'subtitle'
        if (key === 'sub_title') {
          normalized['subtitle'] = obj[key]!;
        } else {
          normalized[camelize(key)] = obj[key]!;
        }
      }
    }
    return normalized;
  }
}
